import { Chat, DBInstance } from '@repo/db'
import {
  ComponentInfoChatVO,
  ComponentMessageVO,
  ComponentNotificationVO,
  ComponentTopicMessageVO,
  NotificationType
} from '@repo/types'
import { Client, CompatClient, Frame, Message, StompSubscription } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { handlePayloadByType } from './functions'

export class StompService {
  private _dbInstance?: DBInstance
  private _userTopic?: StompSubscription
  private _chatTopic?: StompSubscription
  private _stompClient?: CompatClient
  private _currentChat?: Chat
  private _currentUserId?: string
  private _reconnectAttempts: number = 0
  private readonly _maxReconnectDelay: number = 8000
  private _userTopicEventHandler?: (message: ComponentNotificationVO) => Promise<void>
  private _chatTopicEventHandler?: (message: ComponentMessageVO) => Promise<void>

  init = async (
    dbInstance: DBInstance,
    wsUrl: string,
    handleConnect?: () => void,
    handleWebSocketError?: () => void
  ) => {
    this._dbInstance = dbInstance
    return new Promise<boolean>((resolve, reject) => {
      this._stompClient = new Client({
        webSocketFactory: () => new SockJS(wsUrl),
        heartbeatIncoming: 20000,
        heartbeatOutgoing: 0,
        reconnectDelay: 0,
        onConnect: (frame: Frame) => {
          this._reconnectAttempts = 0
          handleConnect?.()
          this.onConnect(frame, resolve)
        },
        onStompError: (frame: Frame) => {
          this.onStompError(frame, reject)
        },
        onWebSocketClose: () => {
          handleWebSocketError?.()
          this.exponentialReconnect()
        }
      })

      this._stompClient.activate()
    })
  }

  get stompClient(): CompatClient | undefined {
    return this._stompClient
  }

  subscribeChatTopic = (chat: Chat, onEvent?: (message: ComponentMessageVO) => Promise<void>) => {
    if (this.isStompDisconnected()) return

    if (onEvent) this._chatTopicEventHandler = onEvent

    const { connectionKey, phoneNumber } = chat
    const topic = `/topic/${connectionKey}.${phoneNumber}`
    this._chatTopic = this._stompClient?.subscribe(topic, async (e: any) => this.handleMessageEvent(e, chat))
    this._currentChat = chat
    console.info(`Topic ${topic} subscribed`)
  }

  subscribeUserTopic = (userId: string, onEvent?: (message: ComponentNotificationVO) => Promise<void>) => {
    if (this.isStompDisconnected()) return

    if (onEvent) this._userTopicEventHandler = onEvent

    this._currentUserId = userId
    const topic = `/topic/user.${userId}`
    this._userTopic = this._stompClient?.subscribe(topic, async (e: any) => this.handleUserTopicEvent(e))
    console.info(`Topic ${topic} subscribed`)
  }

  unsubscribeChatTopic = () => {
    this._chatTopic?.unsubscribe()
    this._chatTopic = undefined
    this._currentChat = undefined
    this._chatTopicEventHandler = undefined
    console.info(`Chat Topic unsubscribed`)
  }

  unsubscribeUserTopic = () => {
    this._userTopic?.unsubscribe()
    this._userTopic = undefined
    this._currentUserId = undefined
    this._userTopicEventHandler = undefined
    console.info(`User Topic unsubscribed`)
  }

  cleanStomp = () => {
    this.unsubscribeUserTopic()
    this.unsubscribeChatTopic()
    this.stompClient?.deactivate().then(() => {
      console.info('Stomp disconnected')
    })
  }

  private readonly onConnect = (frame: Frame, resolve: (value: boolean | PromiseLike<boolean>) => void) => {
    this.resubscribe()
    console.info('[STOMP]', frame.command)
    resolve(true)
  }

  private readonly resubscribe = () => {
    if (this._currentChat) {
      this.subscribeChatTopic(this._currentChat)
    }

    if (this._currentUserId) {
      this.subscribeUserTopic(this._currentUserId)
    }
  }

  private readonly onStompError = (frame: Frame, reject: (value: boolean | PromiseLike<boolean>) => void) => {
    console.error('[STOMP] Error:', frame.headers.message)
    reject(false)
  }

  private _queue: Promise<void> = Promise.resolve()

  private readonly handleUserTopicEvent = async (e: Message) => {
    const body = JSON.parse(e.body) as ComponentNotificationVO
    const type = body.type
    const payload = body.chat

    this._queue = this._queue
      .then(async () => this._processEvent(type, payload, body))
      .catch(err => {
        console.error('[STOMP] Error:', err)
      })
  }

  private async _processEvent(type: NotificationType, payload: ComponentInfoChatVO, body: ComponentNotificationVO) {
    await this._sleep(100)

    await handlePayloadByType(type, payload, this._dbInstance)
    await this._userTopicEventHandler?.(body)
  }

  private readonly handleMessageEvent = async (e: Message, chat: Chat) => {
    const body = JSON.parse(e.body) as ComponentTopicMessageVO

    await this._dbInstance?.messageRepository.addMessage(chat, body.message)
    await this._chatTopicEventHandler?.(body.message)
    await chat.updateBlockedInfo(body)
  }

  public isChatActive = (connectionKey: string, phoneNumber: string) => {
    if (!this._currentChat) return false
    return this._currentChat.connectionKey === connectionKey && this._currentChat.phoneNumber === phoneNumber
  }

  private readonly isStompDisconnected = () => {
    if (!this?._stompClient?.connected) {
      console.debug('[STOMP] Disconnected')
      return true
    }
    return false
  }

  private readonly exponentialReconnect = () => {
    if (!this.isStompDisconnected) return

    const baseDelay = 1000 * Math.pow(2, this._reconnectAttempts)
    const effectiveDelay = Math.min(baseDelay, this._maxReconnectDelay)

    this._reconnectAttempts++

    console.info(`[STOMP] Reconnection attempt ${this._reconnectAttempts} in ${effectiveDelay}ms`)

    setTimeout(() => {
      this._stompClient.deactivate().then(() => {
        this._stompClient.activate()
      })
    }, effectiveDelay)
  }

  private async _sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
